import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "staff-page" }
const _hoisted_2 = { class: "page-heading" }
const _hoisted_3 = { class: "flex flex-wrap gap-2" }
const _hoisted_4 = {
  key: 0,
  role: "alert",
  class: "ui-alert ui-alert--error mb-4"
}
const _hoisted_5 = {
  key: 1,
  class: "ui-empty"
}
const _hoisted_6 = {
  key: 0,
  class: "ui-empty"
}
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import moment from 'moment';
import { useMainStore } from '@/store';
import ExpirationsFilters, {
  ExpirationsFilterModel,
  PeriodMode,
} from '@/components/ExpirationsFilters.vue';
import ExpirationCard from '@/components/ExpirationCard.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'ExpirationsView',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const store = useMainStore();

const products = ref<Product[]>([]);
const companies = ref<Company[]>([]);
const items = ref<ExpirationItem[]>([]);
const summary = ref({ overdue: 0, within_period: 0 });
const until = ref<string | null>(null);
const loading = ref(false);
const errorMessage = ref<string | null>(null);

function filtersFromQuery(): ExpirationsFilterModel {
  const q = route.query;
  const onlyOverdue = q.only_overdue === '1';
  const untilDate = typeof q.until_date === 'string' ? q.until_date : null;
  const withinDaysRaw = typeof q.within_days === 'string' ? Number(q.within_days) : null;
  const type = (
    ['all', 'support', 'module'].includes(String(q.type)) ? q.type : 'all'
  ) as ExpirationsFilterModel['type'];
  const productId = q.product ? Number(q.product) : null;
  const supporterId = q.supporter ? Number(q.supporter) : null;
  const mine = q.mine === '1';
  const hideOverdue = q.hide_overdue === '1' || q.include_overdue === '0';

  let periodMode: PeriodMode = '30';
  let withinDays: number | null = 30;

  if (onlyOverdue) {
    periodMode = 'overdue';
    withinDays = null;
  } else if (untilDate) {
    periodMode = 'custom';
    withinDays = null;
  } else if (withinDaysRaw != null && !Number.isNaN(withinDaysRaw)) {
    withinDays = withinDaysRaw;
    if (withinDays === 7 || withinDays === 30 || withinDays === 90) {
      periodMode = String(withinDays) as PeriodMode;
    } else {
      periodMode = 'custom';
    }
  }

  return {
    periodMode,
    withinDays,
    untilDate,
    type,
    productId: productId && !Number.isNaN(productId) ? productId : null,
    supporterId: supporterId && !Number.isNaN(supporterId) ? supporterId : null,
    mine,
    hideOverdue: onlyOverdue ? false : hideOverdue,
  };
}

const filters = ref<ExpirationsFilterModel>(filtersFromQuery());

const untilLabel = computed(() =>
  until.value ? moment(until.value, 'YYYY-MM-DD').format('DD.MM.YYYY') : null
);

function syncQueryFromFilters() {
  const q: Record<string, string> = {};
  const f = filters.value;

  if (f.periodMode === 'overdue') {
    q.only_overdue = '1';
  } else if (f.untilDate) {
    q.until_date = f.untilDate;
  } else {
    q.within_days = String(f.withinDays ?? 30);
  }

  if (f.type !== 'all') q.type = f.type;
  if (f.productId != null) q.product = String(f.productId);
  if (f.supporterId != null) q.supporter = String(f.supporterId);
  if (f.mine) q.mine = '1';
  if (f.hideOverdue && f.periodMode !== 'overdue') q.hide_overdue = '1';

  router.replace({ query: q });
}

async function loadDicts() {
  try {
    const [catalog, companyList] = await Promise.all([
      axios.get('/api/products/catalog/').then((r) => r.data as Product[]),
      store.getCompanies(),
    ]);
    products.value = catalog;
    companies.value = companyList;
  } catch {
    products.value = [];
    companies.value = [];
  }
}

async function loadExpirations() {
  loading.value = true;
  errorMessage.value = null;
  try {
    const f = filters.value;
    const params: Record<string, string | number> = {
      mine: f.mine ? 1 : 0,
      type: f.type,
    };
    if (f.productId != null) params.product = f.productId;
    if (f.supporterId != null) params.supporter = f.supporterId;

    if (f.periodMode === 'overdue') {
      params.only_overdue = 1;
    } else if (f.untilDate) {
      params.until_date = f.untilDate;
      params.hide_overdue = f.hideOverdue ? 1 : 0;
    } else {
      params.within_days = f.withinDays ?? 30;
      params.hide_overdue = f.hideOverdue ? 1 : 0;
    }

    const data = (await axios.get('/api/expirations/', { params })).data as ExpirationsResponse;
    items.value = data.items;
    summary.value = data.summary;
    until.value = data.until;
  } catch (e: any) {
    items.value = [];
    summary.value = { overdue: 0, within_period: 0 };
    until.value = null;
    errorMessage.value = e?.response?.data?.detail || 'Не удалось загрузить сроки';
  } finally {
    loading.value = false;
  }
}

watch(
  filters,
  () => {
    syncQueryFromFilters();
    loadExpirations();
  },
  { deep: true }
);

onMounted(async () => {
  syncQueryFromFilters();
  await loadDicts();
  await loadExpirations();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _cache[2] || (_cache[2] = _createElementVNode("div", null, [
        _createElementVNode("div", { class: "page-eyebrow" }, "Поддержка и лицензии"),
        _createElementVNode("h1", null, "Сроки")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(Badge), { variant: "destructive" }, {
          default: _withCtx(() => [
            _createTextVNode("Просрочено: " + _toDisplayString(summary.value.overdue), 1)
          ]),
          _: 1
        }),
        _createVNode(_unref(Badge), { variant: "secondary" }, {
          default: _withCtx(() => [
            _createTextVNode("В периоде: " + _toDisplayString(summary.value.within_period), 1)
          ]),
          _: 1
        }),
        (untilLabel.value)
          ? (_openBlock(), _createBlock(_unref(Badge), {
              key: 0,
              variant: "outline"
            }, {
              default: _withCtx(() => [
                _createTextVNode("До " + _toDisplayString(untilLabel.value), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(Badge), { variant: "outline" }, {
          default: _withCtx(() => [
            _createTextVNode("Всего: " + _toDisplayString(items.value.length), 1)
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(ExpirationsFilters, {
      model: filters.value,
      "onUpdate:model": _cache[0] || (_cache[0] = ($event: any) => ((filters).value = $event)),
      products: products.value,
      companies: companies.value
    }, null, 8, ["model", "products", "companies"]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("span", null, _toDisplayString(errorMessage.value), 1),
          _createVNode(_unref(Button), {
            variant: "ghost",
            size: "sm",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (errorMessage.value = null))
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("Закрыть", -1)
            ]))]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(_unref(Spinner)),
          _cache[4] || (_cache[4] = _createElementVNode("span", null, "Загрузка сроков…", -1))
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
          (!items.value.length)
            ? (_openBlock(), _createElementBlock("p", _hoisted_6, [
                _cache[5] || (_cache[5] = _createTextVNode(" Нет истекающих сроков по выбранным фильтрам. ", -1)),
                (filters.value.mine)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, "Попробуйте «Все»."))
                  : (filters.value.hideOverdue)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, " Снимите «Скрыть просроченные» или выберите «Просрочено». "))
                    : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createBlock(ExpirationCard, {
                key: 1,
                items: items.value
              }, null, 8, ["items"]))
        ], 64))
  ]))
}
}

})